import { normalizeIngredientName } from '../ingredients/normalize-name.js';
import { parseShoppingUtterance } from '../shopping/utterance.js';
/** Tokens shorter than this ("ei", "oil") only match exactly, never as substrings. */
const MIN_FUZZY_TOKEN = 4;
const PATTERNS = {
    de: [
        // "ich habe Milch und Eier", "hab ich Milch", "habe Milch", "wir haben Brot"
        /^(?:ich|wir)\s+(?:habe|hab|haben)\s+(?:jetzt\s+|schon\s+|nun\s+)?(.+)$/iu,
        /^(?:habe|hab|haben)\s+(?:ich|wir)\s+(?:jetzt\s+|schon\s+)?(.+)$/iu,
        // "erledigt Milch", "abgehakt Milch", "hab Milch", "gekauft Milch"
        /^(?:erledigt|abgehakt|abhaken|gekauft|eingepackt|hab|habe)\s+(.+)$/iu,
        // "Milch erledigt", "Milch und Eier hab ich", "Milch ist im Wagen", "Milch abhaken"
        /^(.+?)\s+(?:ist|sind)?\s*(?:erledigt|abgehakt|abhaken|gekauft|eingepackt|im wagen|im korb|hab ich|habe ich|haben wir|hab ich schon|habe ich schon)$/iu,
    ],
    en: [
        // "I have milk and eggs", "I got milk", "we've got bread", "I've picked up milk"
        /^(?:i|we)(?:'ve|\s+have)?\s+(?:got|have|bought|picked up|grabbed)\s+(?:the\s+|some\s+)?(.+)$/iu,
        // "done milk", "got milk", "check off milk", "check milk"
        /^(?:done|got|bought|check off|check|tick off|tick)\s+(?:the\s+)?(.+)$/iu,
        // "milk done", "milk and eggs got it", "milk is in the cart", "milk checked"
        /^(.+?)\s+(?:is|are)?\s*(?:done|got it|bought|checked|ticked|in the cart|in the basket|in the trolley)$/iu,
    ],
};
const TRAILING_PUNCTUATION = /[\s.,;:!?…]+$/u;
/** Does the spoken name match an item key? The pantry's rule: exact, contained, or a shared long word. */
function matches(spokenKey, itemKey) {
    if (!spokenKey || !itemKey)
        return false;
    if (spokenKey === itemKey)
        return true;
    if (spokenKey.length >= MIN_FUZZY_TOKEN && itemKey.includes(spokenKey))
        return true;
    if (itemKey.length >= MIN_FUZZY_TOKEN && spokenKey.includes(itemKey))
        return true;
    const spokenTokens = spokenKey.split(' ').filter((t) => t.length >= MIN_FUZZY_TOKEN);
    const itemTokens = itemKey.split(' ');
    return spokenTokens.some((t) => itemTokens.includes(t));
}
/**
 * Recognises a check-off sentence and resolves the names against `openItems`. Returns null
 * when the transcript is not shaped like one.
 */
export function parseCheckCommand(transcript, locale, openItems) {
    const text = transcript.replace(/\s+/g, ' ').replace(TRAILING_PUNCTUATION, '').trim();
    if (!text)
        return null;
    let named = null;
    for (const pattern of PATTERNS[locale]) {
        const m = pattern.exec(text);
        if (m?.[1]) {
            named = m[1].trim();
            break;
        }
    }
    if (!named)
        return null;
    // The names are split like a dictation ("Milch und Eier" → two), quantities are dropped.
    const parts = parseShoppingUtterance(named, locale, openItems.map((i) => i.name)).items;
    if (!parts.length)
        return null;
    const keyed = openItems.map((item) => ({ item, key: normalizeIngredientName(item.name) }));
    const matched = [];
    const unmatched = [];
    const taken = new Set();
    for (const part of parts) {
        const spokenKey = normalizeIngredientName(part.name);
        // The best hit: an exact key first, then the shortest key that matches (the most specific).
        const hits = keyed
            .filter(({ item, key }) => !taken.has(item.id) && matches(spokenKey, key))
            .sort((a, b) => a.key === spokenKey ? -1 : b.key === spokenKey ? 1 : a.key.length - b.key.length);
        const hit = hits[0];
        if (hit) {
            taken.add(hit.item.id);
            matched.push(hit.item);
        }
        else
            unmatched.push(part.name);
    }
    return { matched, unmatched };
}
//# sourceMappingURL=parse-check-command.js.map